import React, { ReactNode } from 'react'

import {
  HouseAvailabilityIndividualNightlyTripOptionsOutputDto,
  HouseAvailabilityIndividualNightlyTripOutputDto,
  HouseAvailabilityIndividualOutputDto,
} from '../../../../../../../../lib/graphql'
import { Icon } from '../../../../../../../atoms'
import { formatMoney } from '../../../../../../house-options/shared/utils/format/format-money/format-money'
import { HouseContextIndividual } from '../../../../../helpers/assert-house-price-type'
import { getSeasonPriceKey } from '../../../../shared/calendar/utils/get-price-key-from-date'
import { BigCalendarFormDataIndividual } from '../../../../shared/calendar/utils/use-big-calendar-form'

type BigCalendarOptionsIndividualCellProps = {
  house: HouseContextIndividual
  cellDate: Date
}
export const BigCalendarOptionsIndividualCell = ({
  house,
  cellDate,
}: BigCalendarOptionsIndividualCellProps) => {
  const priceKey = getSeasonPriceKey({ date: cellDate, options: house.options })

  const singleBedroomPrice =
    house.options.nightlyTrip.options?.seasonalPrices[0].singleBedroom[priceKey]

  const sharedBedroomPrice =
    house.options.nightlyTrip.options?.seasonalPrices[0].sharedBedroom[priceKey]

  const nightlyTrip: BigCalendarFormDataIndividual['nightlyTrip'] = {
    options: { sharedBedroomPrice, singleBedroomPrice },
    isEnabled: house.options.nightlyTrip.isEnabled,
  }
  return (
    <div className='flex flex-col items-start gap-2'>
      <BigCalendarNightlyTripChip
        nightlyTrip={nightlyTrip}
        house={house}
        objKey='singleBedroomPrice'
      />
      <BigCalendarNightlyTripChip
        nightlyTrip={nightlyTrip}
        house={house}
        objKey='sharedBedroomPrice'
      />
    </div>
  )
}

type BigCalendarAvailabilityIndividualCellProps = {
  availability: HouseAvailabilityIndividualOutputDto
  house: HouseContextIndividual
}
export const BigCalendarAvailabilityIndividualCell = ({
  availability,
  house,
}: BigCalendarAvailabilityIndividualCellProps) => {
  const { nightlyTrip } = availability

  return (
    <div className='flex flex-col items-center gap-1'>
      <BigCalendarNightlyTripChip
        nightlyTrip={nightlyTrip}
        house={house}
        objKey='singleBedroomPrice'
        colorize={true}
      />
      <BigCalendarNightlyTripChip
        nightlyTrip={nightlyTrip}
        house={house}
        objKey='sharedBedroomPrice'
        colorize={true}
      />
    </div>
  )
}

type BigCalendarNightlyTripSingleBedroomChipProps = {
  nightlyTrip: HouseAvailabilityIndividualNightlyTripOutputDto
  house: HouseContextIndividual
  objKey: keyof Omit<
    HouseAvailabilityIndividualNightlyTripOptionsOutputDto,
    '__typename'
  >
  colorize?: boolean
}
const BigCalendarNightlyTripChip = ({
  nightlyTrip,
  house,
  objKey,
  colorize,
}: BigCalendarNightlyTripSingleBedroomChipProps) => {
  return (
    <BigCalendarChipWrapper colorize={colorize}>
      <Icon
        name={objKey === 'sharedBedroomPrice' ? 'user-filled' : 'people'}
        size={12}
      />

      <span>
        {formatMoney({
          value: nightlyTrip.options?.[objKey],
          currency: house.currencyArea.currency,
          options: { hideDecimals: true, applyFactor: true },
        })}
      </span>
    </BigCalendarChipWrapper>
  )
}

type BigCalendarChipWrapperProps = {
  children: ReactNode
  colorize?: boolean
}
const BigCalendarChipWrapper = ({
  children,
  colorize,
}: BigCalendarChipWrapperProps) => {
  const bgColor = colorize ? 'bg-primary-400' : 'bg-grey-400'

  return (
    <div
      className={`flex h-5 w-[72px] items-center justify-center gap-1 rounded-lg ${bgColor} px-2 py-1 text-xs`}
    >
      {children}
    </div>
  )
}
